import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '../schemas/loginSchema';
import { login } from '../api/auth';
import { setCompanyId, setCampaignCode } from '../utils/storage';
import { checkCampaignActive } from '../api/campaign';
import LabeledTextField from '../components/LabeledTextField';
import {
  Box,
  Button,
  Typography,
  Alert,
  Link,
  Stack,
  Divider,
  Container,
  Paper,
} from '@mui/material';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const companyIdParam = params.get('companyId');
    const priceIdParam = params.get('price_id');

    if (companyIdParam) {
      setCompanyId(companyIdParam);
    }

    if (priceIdParam) {
      setCampaignCode(priceIdParam);

      checkCampaignActive(priceIdParam)
        .then((res) => {
          if (res.is_active === 0) {
            navigate('/error');
          }
        })
        .catch(() => {
          navigate('/error');
        });
    }
  }, [location.search, navigate]);

  const onSubmit = async (data) => {
    try {
      const res = await login(data.email, data.password);

      if (res.success) {
        const { token, role } = res;
        localStorage.setItem('token', token);

        if (role === 'admin') {
          navigate('/adminpage');
        } else if (role === 'user') {
          navigate('/mypage');
        } else {
          navigate('/implpage');
        }
      } else {
        setFormError('root', {
          type: 'manual',
          message: 'ログインに失敗しました',
        });
      }
    } catch {
      setFormError('root', {
        type: 'manual',
        message: '通信エラー',
      });
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        py: { xs: '7vh', sm: '10vh', md: '10vh', lg: '10vh' },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 3,
          marginBottom: '2vh',
        }}
      >
        <Typography
          variant="h3"
          align="center"
          sx={{
            margin: 2,
          }}
        >
          ログイン
        </Typography>

        <Divider
          sx={{
            width: '100%',
            borderColor: 'secondary.main',
            borderBottomWidth: 3,
          }}
        />

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            maxWidth: 'none',
            boxShadow: 'none',
            minHeight: 'fit-content',
            padding: 0,
            mt: '5dvh',
            alignItems: 'flex-start',
          }}
        >
          <LabeledTextField
            label="メールアドレス"
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="xxx@example.com"
            register={register}
            error={errors.email}
          />

          <LabeledTextField
            label="パスワード"
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="パスワードを入力してください"
            register={register}
            error={errors.password}
          />

          {errors.root && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.root.message}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            margin="none"
            sx={{ mt: 3, mb: 2, borderRadius: 5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'ログイン中...' : 'ログイン'}
          </Button>

          <Box sx={{ marginTop: 2 }}>
            <Stack direction="column" spacing={1} alignItems="flex-start">
              <Link
                component="button"
                variant="body2"
                color="info"
                onClick={() => navigate('/forgot-id')}
              >
                メールアドレスを忘れた方はこちら
              </Link>

              <Link
                component="button"
                variant="body2"
                color="info"
                onClick={() => navigate('/forgot-password')}
              >
                パスワードを忘れた方はこちら
              </Link>
            </Stack>
          </Box>

          <Divider
            sx={{
              borderColor: 'primary.main',
              width: '100%',
            }}
          />

          <Box sx={{ mt: 2, width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              新規会員の登録
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              color="primary"
              onClick={() => navigate('/signup')}
              sx={{ mt: 1, borderRadius: 5 }}
            >
              新規会員の登録
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
